import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, Button, TextField, FormControl,
  InputLabel, Select, MenuItem, Grid, Paper, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Pagination, Alert,
  CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment, useMediaQuery, alpha,
  Avatar, Divider, SwipeableDrawer, LinearProgress,
  Tooltip, Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
  Print as PrintIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Computer as ComputerIcon,
  AttachMoney as MoneyIcon,
  Devices as DevicesIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import Layout from '../components/Layout';
import StatsCard from '../components/StatsCard';
import PageHeader from '../components/PageHeader';

function OsList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [osList, setOsList] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOS, setSelectedOS] = useState(null);

  // Filtros - padrão: OS Abertas
  const [filters, setFilters] = useState({
    status: 'aberta',
    cliente_id: '',
    data_inicio: '',
    data_fim: ''
  });

  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    perPage: isMobile ? 5 : 10
  });

  useEffect(() => {
    carregarClientes();
    carregarOS();
  }, [pagination.page, filters.status, filters.cliente_id, filters.data_inicio, filters.data_fim]);

  const carregarClientes = async () => {
    try {
      const response = await api.get('/clientes?limit=100');
      setClientes(response.data.clientes || response.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const carregarOS = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.perPage,
        ...filters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined || params[key] === null) {
          delete params[key];
        }
      });

      const response = await api.get('/os', { params });

      const data = response.data.ordens || response.data || [];
      setOsList(Array.isArray(data) ? data : []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || response.data.count || (Array.isArray(data) ? data.length : 0)
      }));
    } catch (error) {
      console.error('Erro ao carregar OS:', error);
      toast.error('Erro ao carregar ordens de serviço');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    carregarOS();
    setFilterOpen(false);
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'aberta',
      cliente_id: '',
      data_inicio: '',
      data_fim: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  const getStatusColor = (status) => {
    const colors = {
      aberta: 'warning',
      em_andamento: 'info',
      finalizada: 'success',
      cancelada: 'error',
      pendente: 'warning',
      aprovada: 'success'
    };
    return colors[status?.toLowerCase()] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      aberta: 'Aberta',
      em_andamento: 'Em Andamento',
      finalizada: 'Finalizada',
      cancelada: 'Cancelada',
      pendente: 'Pendente',
      aprovada: 'Aprovada'
    };
    return labels[status?.toLowerCase()] || status;
  };

  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case 'aberta': return <PendingIcon />;
      case 'em_andamento': return <PendingIcon />;
      case 'finalizada': return <CheckCircleIcon />;
      case 'cancelada': return <CancelIcon />;
      default: return <AssignmentIcon />;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (date) => {
    if (!date) return '—';
    try {
      const parsed = typeof date === 'string' ? parseISO(date) : new Date(date);
      if (!isValid(parsed)) return '—';
      return format(parsed, 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return '—';
    }
  };

  const handlePrint = (os) => {
    const token = localStorage.getItem('@Jobson:token');
    if (!token) {
      toast.error('Sessão expirada. Faça login novamente.');
      return;
    }
    
    // Verificar se é uma OS em lote (tem mais de um computador ou é marcada como lote)
    // A API retorna total_computadores se for lote
    const isLote = os.total_computadores && os.total_computadores > 1;
    
    if (isLote) {
      window.open(`/api/os-lote/${os.id}/print-lote?token=${token}`, '_blank');
    } else {
      window.open(`/api/os/${os.id}/print?token=${token}`, '_blank');
    }
  };

  const handleOsClick = (os) => {
    if (isMobile) {
      setSelectedOS(os);
      setDrawerOpen(true);
    } else {
      navigate(`/os/${os.id}`);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedOS(null);
  };

  // Estatísticas
  const totalOS = osList.length;
  const abertas = osList.filter(o => o.status?.toLowerCase() === 'aberta').length;
  const finalizadas = osList.filter(o => o.status?.toLowerCase() === 'finalizada').length;
  const emAndamento = osList.filter(o => o.status?.toLowerCase() === 'em_andamento').length;

  return (
    <Layout title="Ordens de Serviço">
      <PageHeader
        title="Ordens de Serviço"
        subtitle={`${totalOS} OS encontradas • ${abertas} abertas`}
        icon={<AssignmentIcon sx={{ color: '#39FF14' }} />}
      >
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/os/nova')}
            sx={{
              bgcolor: '#0A2647',
              '&:hover': { bgcolor: '#1a3b5c' },
              color: 'white'
            }}
          >
            {isMobile ? 'Nova' : 'Nova OS'}
          </Button>
          <Button
            variant="contained"
            startIcon={<DevicesIcon />}
            onClick={() => navigate('/os/lote')}
            sx={{
              bgcolor: '#2e7d32',
              '&:hover': { bgcolor: '#1b5e20' },
              color: 'white'
            }}
          >
            {isMobile ? 'Lote' : 'OS em Lote'}
          </Button>
        </Box>
      </PageHeader>

      {/* Cards de Estatísticas */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Total"
            value={totalOS}
            icon={<AssignmentIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Abertas"
            value={abertas}
            icon={<PendingIcon />}
            color="warning"
            highlight={abertas > 0}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Em Andamento"
            value={emAndamento}
            icon={<PendingIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCard
            title="Finalizadas"
            value={finalizadas}
            icon={<CheckCircleIcon />}
            color="success"
          />
        </Grid>
      </Grid>

      <Card sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 3 }}>
        {/* Filtros - Versão Desktop */}
        {!isMobile ? (
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={filters.status}
                    label="Status"
                    onChange={handleFilterChange}
                    sx={{ borderRadius: 3 }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="aberta">Aberta</MenuItem>
                    <MenuItem value="em_andamento">Em Andamento</MenuItem>
                    <MenuItem value="finalizada">Finalizada</MenuItem>
                    <MenuItem value="cancelada">Cancelada</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Cliente</InputLabel>
                  <Select
                    name="cliente_id"
                    value={filters.cliente_id}
                    label="Cliente"
                    onChange={handleFilterChange}
                    sx={{ borderRadius: 3 }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {clientes.map(c => (
                      <MenuItem key={c.id} value={c.id}>{c.nome_razao}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Data Início"
                  name="data_inicio"
                  type="date"
                  value={filters.data_inicio}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{ borderRadius: 3 }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Data Fim"
                  name="data_fim"
                  type="date"
                  value={filters.data_fim}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{ borderRadius: 3 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    size="small"
                    sx={{ borderRadius: 2 }}
                  >
                    Limpar Filtros
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      bgcolor: '#0A2647',
                      '&:hover': { bgcolor: '#1a3b5c' }
                    }}
                  >
                    Buscar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFilterOpen(true)}
              sx={{ borderRadius: 3, py: 1.5 }}
            >
              Filtros {filters.status !== 'aberta' && '(Ativos)'}
            </Button>
          </Box>
        )}

        {/* Lista Mobile vs Tabela Desktop */}
        {isMobile ? (
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress />
              </Box>
            ) : osList.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Nenhuma ordem de serviço encontrada
              </Alert>
            ) : (
              <AnimatePresence>
                {osList.map((os, index) => (
                  <motion.div
                    key={os.id}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card
                      sx={{
                        mb: 1.5,
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.06)',
                        '&:active': { transform: 'scale(0.98)' },
                        cursor: 'pointer'
                      }}
                      onClick={() => handleOsClick(os)}
                    >
                      <Box sx={{ p: 1.5 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar sx={{
                              bgcolor: alpha('#0A2647', 0.1),
                              color: '#0A2647',
                              width: 40,
                              height: 40
                            }}>
                              {getStatusIcon(os.status)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0A2647' }}>
                                #{os.numero_os}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {os.cliente_nome || os.cliente?.nome_razao || 'Sem cliente'}
                              </Typography>
                              {os.total_computadores && os.total_computadores > 1 && (
                                <Chip
                                  label={`${os.total_computadores} PCs`}
                                  size="small"
                                  sx={{ 
                                    mt: 0.5, 
                                    bgcolor: '#2e7d32', 
                                    color: 'white',
                                    fontSize: '0.6rem',
                                    height: 18
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                          <Chip
                            label={getStatusLabel(os.status)}
                            size="small"
                            color={getStatusColor(os.status)}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 500,
                              ...(os.status?.toLowerCase() === 'aberta' && {
                                bgcolor: alpha('#f39c12', 0.15),
                                color: '#b7950b'
                              })
                            }}
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                          <Box display="flex" gap={2} flexWrap="wrap">
                            {os.computador_nome && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <ComputerIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption">{os.computador_nome}</Typography>
                              </Box>
                            )}
                            {os.data_abertura && (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption">{formatDate(os.data_abertura)}</Typography>
                              </Box>
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0A2647' }}>
                            {formatCurrency(os.valor_total)}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}

            {pagination.total > pagination.perPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(pagination.total / pagination.perPage)}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: 2,
                      fontSize: '0.75rem'
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#0A2647', 0.04) }}>
                    <TableCell><strong>OS</strong></TableCell>
                    <TableCell><strong>Cliente</strong></TableCell>
                    <TableCell><strong>Computador</strong></TableCell>
                    <TableCell><strong>Data</strong></TableCell>
                    <TableCell align="right"><strong>Valor</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Ações</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : osList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          Nenhuma ordem de serviço encontrada
                        </Alert>
                      </TableCell>
                    </TableRow>
                  ) : (
                    osList.map((os) => (
                      <TableRow
                        key={os.id}
                        hover
                        sx={{
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: alpha('#0A2647', 0.02)
                          },
                          cursor: 'pointer'
                        }}
                        onClick={() => navigate(`/os/${os.id}`)}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0A2647' }}>
                              #{os.numero_os}
                            </Typography>
                            {os.total_computadores && os.total_computadores > 1 && (
                              <Chip
                                label={`${os.total_computadores} PCs`}
                                size="small"
                                sx={{ 
                                  bgcolor: '#2e7d32', 
                                  color: 'white',
                                  fontSize: '0.6rem',
                                  height: 18
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2">{os.cliente_nome || os.cliente?.nome_razao || '—'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {os.computador_nome || os.computador?.nome_identificacao || '—'}
                        </TableCell>
                        <TableCell>
                          {formatDate(os.data_abertura)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0A2647' }}>
                            {formatCurrency(os.valor_total)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(os.status)}
                            size="small"
                            color={getStatusColor(os.status)}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 500,
                              ...(os.status?.toLowerCase() === 'aberta' && {
                                bgcolor: alpha('#f39c12', 0.15),
                                color: '#b7950b'
                              })
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Imprimir">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePrint(os);
                              }}
                              sx={{ color: '#0A2647' }}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {pagination.total > pagination.perPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(pagination.total / pagination.perPage)}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Card>

      {/* Drawer Mobile - Detalhes da OS */}
      <SwipeableDrawer
        anchor="bottom"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onOpen={() => {}}
        sx={{
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '85vh',
            p: 2
          }
        }}
      >
        {selectedOS && (
          <Box>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              pb: 1,
              borderBottom: '1px solid rgba(0,0,0,0.06)'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2647' }}>
                OS #{selectedOS.numero_os}
                {selectedOS.total_computadores && selectedOS.total_computadores > 1 && (
                  <Chip
                    label={`${selectedOS.total_computadores} PCs`}
                    size="small"
                    sx={{ ml: 1, bgcolor: '#2e7d32', color: 'white' }}
                  />
                )}
              </Typography>
              <IconButton onClick={handleCloseDrawer} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Cliente</Typography>
                    <Typography variant="body2">{selectedOS.cliente_nome || selectedOS.cliente?.nome_razao || 'Não informado'}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <ComputerIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Computador</Typography>
                    <Typography variant="body2">{selectedOS.computador_nome || selectedOS.computador?.nome_identificacao || 'Não informado'}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CalendarIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Data de Abertura</Typography>
                    <Typography variant="body2">{formatDate(selectedOS.data_abertura)}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <MoneyIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Valor Total</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0A2647' }}>
                      {formatCurrency(selectedOS.valor_total)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {selectedOS.observacoes && (
                <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#0A2647', 0.02) }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AssignmentIcon sx={{ color: '#0A2647', fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Observações</Typography>
                      <Typography variant="body2">{selectedOS.observacoes}</Typography>
                    </Box>
                  </Box>
                </Paper>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<VisibilityIcon />}
                  onClick={() => {
                    handleCloseDrawer();
                    navigate(`/os/${selectedOS.id}`);
                  }}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#0A2647',
                    color: '#ffffff',
                    '&:hover': {
                      bgcolor: '#1a3b5c',
                      color: '#ffffff'
                    }
                  }}
                >
                  Ver Detalhes
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={() => {
                    handleCloseDrawer();
                    handlePrint(selectedOS);
                  }}
                  sx={{
                    borderRadius: 2,
                    color: '#0A2647',
                    borderColor: '#0A2647',
                    '&:hover': {
                      borderColor: '#0A2647',
                      bgcolor: alpha('#0A2647', 0.04)
                    }
                  }}
                >
                  Imprimir
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </SwipeableDrawer>

      {/* Dialog de Filtros Mobile */}
      <Dialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: '#0A2647',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filtros
          </Typography>
          <IconButton onClick={() => setFilterOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="medium">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={filters.status}
                  label="Status"
                  onChange={handleFilterChange}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="aberta">Aberta</MenuItem>
                  <MenuItem value="em_andamento">Em Andamento</MenuItem>
                  <MenuItem value="finalizada">Finalizada</MenuItem>
                  <MenuItem value="cancelada">Cancelada</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="medium">
                <InputLabel>Cliente</InputLabel>
                <Select
                  name="cliente_id"
                  value={filters.cliente_id}
                  label="Cliente"
                  onChange={handleFilterChange}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {clientes.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.nome_razao}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="medium"
                label="Data Início"
                name="data_inicio"
                type="date"
                value={filters.data_inicio}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="medium"
                label="Data Fim"
                name="data_fim"
                type="date"
                value={filters.data_fim}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 1 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSearch}
            sx={{
              borderRadius: 2,
              py: 1.5,
              bgcolor: '#0A2647',
              '&:hover': { bgcolor: '#1a3b5c' }
            }}
          >
            Aplicar Filtros
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClearFilters}
            sx={{
              borderRadius: 2,
              py: 1.5,
              color: '#0A2647',
              borderColor: '#0A2647'
            }}
          >
            Limpar Filtros
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default OsList;
